import React from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, menuItems } from '../../data/site-content';
import { Mail, Phone, MapPin, ChevronRight, Award, ExternalLink } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#FAF7F2] border-t border-[#E8DDCE] pt-12 pb-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Logo & Info */}
          <div className="flex flex-col gap-6">
            <div className="-mt-4">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8d119a2b-621a-4bb4-bc1e-9933e5786d8e.png" 
                  alt="Corehypnose" 
                  className="h-28 w-auto object-contain"
                />
              </Link>
              <h3 className="text-[#123F13] font-bold text-lg uppercase tracking-wider leading-tight">
                {siteConfig.contact.companyName}
              </h3>
              <p className="text-[#3B352F]/70 text-sm mt-2">{siteConfig.contact.role}</p>
            </div>
            <div className="flex flex-col gap-3">
              {siteConfig.contact.titles.map((title, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-[#3B352F]/60">
                  <Award size={14} className="mt-0.5 text-[#8C2F12] shrink-0" />
                  <span>{title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hurtiglinks */}
          <div>
            <h4 className="font-bold text-[#3B352F] uppercase tracking-widest text-sm mb-6">Sider</h4>
            <ul className="grid grid-cols-1 gap-3">
              {menuItems.slice(0, 8).map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-sm text-[#3B352F]/70 hover:text-[#123F13] flex items-center gap-2 transition-colors">
                    <ChevronRight size={14} className="text-[#E8DDCE]" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Flere links */}
          <div>
            <h4 className="font-bold text-[#3B352F] uppercase tracking-widest text-sm mb-6">Praktisk</h4>
            <ul className="grid grid-cols-1 gap-3">
              <li>
                <Link to="/priser" className="text-sm text-[#3B352F]/70 hover:text-[#123F13] flex items-center gap-2 transition-colors">
                  <ChevronRight size={14} className="text-[#E8DDCE]" />
                  Priser
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-[#3B352F]/70 hover:text-[#123F13] flex items-center gap-2 transition-colors">
                  <ChevronRight size={14} className="text-[#E8DDCE]" />
                  Ofte stillede spørgsmål (FAQ)
                </Link>
              </li>
              <li>
                <Link to="/privatlivspolitik" className="text-sm text-[#3B352F]/70 hover:text-[#123F13] flex items-center gap-2 transition-colors">
                  <ChevronRight size={14} className="text-[#E8DDCE]" />
                  Privatlivspolitik
                </Link>
              </li>
              <li>
                <Link to="/kontakt#akut" className="text-sm text-red-700 hover:text-red-800 font-medium flex items-center gap-2 transition-colors">
                  <ChevronRight size={14} className="text-[#E8DDCE]" />
                  Akut hjælp ved psykisk krise
                </Link>
              </li>
            </ul>
          </div>

          {/* Kontakt Info */}
          <div>
            <h4 className="font-bold text-[#3B352F] uppercase tracking-widest text-sm mb-6">Kontakt</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#123F13] mt-1 shrink-0" />
                <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                  {siteConfig.contact.address}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#123F13] shrink-0" />
                <a href={`tel:${siteConfig.contact.phone.replace(/\s+/g, '')}`} className="text-sm text-[#3B352F]/70 hover:text-[#123F13]">
                  Telefon: {siteConfig.contact.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#123F13] shrink-0" />
                <a href={`mailto:${siteConfig.contact.email}`} className="text-sm text-[#3B352F]/70 hover:text-[#123F13]">
                  {siteConfig.contact.email}
                </a>
              </div>
              <div className="flex items-center gap-3 border-t border-[#E8DDCE]/50 pt-3 mt-1">
                <span className="text-xs font-bold text-[#3B352F]/60 uppercase tracking-wider">
                  CVR: {siteConfig.contact.cvr}
                </span>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-[#E8DDCE]">
              <Link to="/bestil-tid" className="inline-block bg-[#123F13] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#123F13]/90 transition-all">
                Book tid online
              </Link>
            </div>
          </div>

        </div>

        {/* Faglige certificeringer og kvalitetsmærker (Mørkegrøn baggrund med guldlogoer) */}
        <div className="mt-14 pt-8 border-t border-[#E8DDCE]">
          <div className="bg-[#123F13] text-white rounded-2xl p-6 md:p-8 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left max-w-xl">
              <span className="text-[#d2b266] uppercase tracking-widest text-[11px] font-bold block mb-1">
                Faglige Certificeringer & Kvalitetssikring
              </span>
              <h4 className="font-serif text-xl md:text-2xl text-white font-medium">
                Dokumenteret faglighed og etiske standarder
              </h4>
              <p className="text-xs md:text-sm text-white/80 mt-2 leading-relaxed">
                Behandling hos Lars Nissen Corell – Psykoterapeut MPF er forankret i den fireårige metakognitive psykoterapeutuddannelse på højeste niveau (Level IV) hos Neokognitivt Institut samt medlemskab af Dansk Psykoterapeutforening.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 md:gap-12 shrink-0">
              {/* NKI Level IV logo */}
              <a 
                href="https://www.neokognitivtinstitut.dk/" 
                target="_blank" 
                rel="noopener noreferrer"
                title="Neokognitivt Institut – Certificeret Metakognitiv Psykoterapeut, Level IV Højeste Niveau"
                className="group flex flex-col items-center text-center transition-transform hover:scale-105"
              >
                <div className="w-28 h-28 md:w-32 md:h-32 flex items-center justify-center p-1">
                  <img 
                    src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/level-4-guld-png-1080x1080-2-1.png" 
                    alt="Neokognitivt Institut – Level IV Højeste Niveau" 
                    className="w-full h-full object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
                  />
                </div>
                <span className="text-[11px] text-[#f1dcab] font-medium mt-1 group-hover:text-white transition-colors flex items-center gap-1">
                  Neokognitivt Institut · Level IV <ExternalLink size={10} />
                </span>
              </a>

              {/* MPF logo */}
              <a 
                href="https://psykoterapeutforeningen.dk/psykoterapeuter/91679-Lars-Nissen-Corell" 
                target="_blank" 
                rel="noopener noreferrer"
                title="Dansk Psykoterapeutforening – Se Lars Nissen Corells officielle MPF-profil"
                className="group flex flex-col items-center text-center transition-transform hover:scale-105"
              >
                <div className="w-28 h-28 md:w-32 md:h-32 flex items-center justify-center p-1">
                  <img 
                    src="/_assets/mpf-guld.png" 
                    alt="Psykoterapeut MPF – Dansk Psykoterapeutforening" 
                    className="w-auto h-28 md:h-32 object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
                  />
                </div>
                <span className="text-[11px] text-[#f1dcab] font-medium mt-1 group-hover:text-white transition-colors flex items-center gap-1">
                  Psykoterapeut MPF · Profil <ExternalLink size={10} />
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[#E8DDCE] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#3B352F]/50">
            © {new Date().getFullYear()} Corehypnose.dk – Alle rettigheder forbeholdes.
          </p>
          <div className="flex gap-6 text-xs text-[#3B352F]/50">
            <Link to="/privatlivspolitik" className="hover:text-[#123F13]">Privatlivspolitik</Link>
            <Link to="/cookies" className="hover:text-[#123F13]">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
